<template>
    <div class="automations-page">
        <loader v-if="is('loading')"></loader>

        <svg style="display: none;">
            <defs>
                <symbol id="arrow-right" viewBox="0 0 8 12">
                    <path d="M1 1L6 6L1 11" stroke="currentColor" stroke-width="2"/>
                </symbol>
            </defs>
        </svg>
        <div class="container-fluid" v-if="!is('loading')">
            <div v-if="type == 'edit'" class="alert alert-warning mb-4">
                {{ t('automations-form-editing_warning') }}
            </div>
            <div class="box">
                <confirm
                    id="confirmEditAutomation"
                    ref="confirmEditAutomation"
                    @cancel="back"
                >
                    <template slot="title">
                        <span v-html="getConfirmOverwriteTitle()"></span>
                    </template>
                    <template slot="body">
                        <span>
                            {{ t('automations-form-confirm_overwrite_body') }}
                        </span>
                    </template>
                </confirm>
                <confirm
                    id="confirmEditActiveCampaign"
                    ref="confirmEditActiveCampaign"
                    @cancel="back"
                >
                    <template slot="title">
                        <span>
                            {{ t('automations-form-confirm_edit_active_title') }}
                        </span>
                    </template>
                    <template slot="body">
                        <span>
                            {{ t('automations-form-confirm_edit_active_body') }}
                        </span>
                    </template>
                </confirm>
                <h1>{{ t(type == "new" ? "automations-form-title_new" : "automations-form-title_edit") }} {{ t('automations-form-title_page_suffix') }}</h1>
                <form action="#">
                    <div class="field">
                        <h2>{{ t('automations-form-title_label') }}</h2>
                        <small>{{ t('automations-form-title_help') }}</small>
                        <input
                            type="text"
                            class="form-control"
                            v-model="title"
                        />
                    </div>
                    <div class="field">
                        <h2>{{ t('automations-form-goal_label') }}</h2>
                        <small>{{ t('automations-form-goal_help') }}</small>
                        <select v-model="goal" class="form-control">
                            <option :value="null">{{ t('automations-form-select') }}</option>
                            <option
                                v-for="(label, goal) in goals"
                                :key="goal"
                                :value="goal"
                            >
                                {{ label }}
                            </option>
                        </select>
                        <template
                            v-if="
                                ['answers', 'custom_event_clients'].includes(
                                    goal,
                                )
                            "
                        >
                            <small>{{ t('automations-form-select_option') }}</small>
                            <select v-model="goalId" class="form-control">
                                <option :value="null">
                                    {{ t('automations-form-any_option') }}
                                </option>
                                <option
                                    v-for="option in goalIdOptions"
                                    :key="option.id"
                                    :value="option.id"
                                >
                                    {{ option.label }}
                                </option>
                            </select>
                        </template>
                        <small>{{ t('automations-form-conversion_period_help') }}</small>
                        <input
                            type="text"
                            class="form-control"
                            :placeholder="t('automations-form-zero_days')"
                            v-model="conversionPeriod"
                        />
                    </div>
                    <div class="trigger-settings">
                        <div class="field">
                            <h2>{{ t('automations-form-trigger_label') }}</h2>
                            <small>{{ t('automations-form-trigger_help') }}</small>
                            <select
                                class="form-control"
                                v-model="trigger"
                                required="required"
                            >
                                <option value=""></option>
                                <option
                                    v-if="$module('automations')"
                                    value="inactivity"
                                >
                                    {{ t('automations-form-triggers-inactivity') }}
                                </option>
                                <option
                                    v-if="$module('automations')"
                                    value="new_order"
                                >
                                    {{ t('automations-form-triggers-new_order') }}
                                </option>
                                <option
                                    v-if="
                                        $module('automations') &&
                                        module('order_status')
                                    "
                                    value="order_status_change"
                                >
                                    {{ t('automations-form-triggers-order_status_change') }}
                                </option>
                                <option
                                    v-if="
                                        $module('automations') &&
                                        module('product_purchase_prediction')
                                    "
                                    value="product_group_stock"
                                >
                                    {{ t('automations-form-triggers-product_group_stock') }}
                                </option>
                                <option
                                    v-if="$module('automations')"
                                    value="periodic"
                                >
                                    {{ t('automations-form-triggers-periodic') }}
                                </option>
                                <option
                                    v-if="$module('order_status')"
                                    value="too_long_on_external_status"
                                >
                                    {{ t('automations-form-triggers-too_long_on_external_status') }}
                                </option>
                                <option
                                    v-if="
                                        $module('automations') &&
                                        module('client_fields')
                                    "
                                    value="client_date"
                                >
                                    {{ t('automations-form-triggers-client_date') }}
                                </option>
                                <option
                                    v-if="$module('automations')"
                                    value="data_filled"
                                >
                                    {{ t('automations-form-triggers-data_filled') }}
                                </option>
                                <option
                                    v-if="
                                        $module('automations') &&
                                        module('custom_events')
                                    "
                                    value="custom_event"
                                >
                                    {{ t('automations-form-triggers-custom_event') }}
                                </option>
                                <option
                                    v-if="
                                        $module('automations') &&
                                        module('custom_events')
                                    "
                                    value="custom_event_without_order"
                                >
                                    {{ t('automations-form-triggers-custom_event_without_order') }}
                                </option>
                                <option
                                    v-if="
                                        $module('automations') &&
                                        module('coupons')
                                    "
                                    value="coupon_expiring"
                                >
                                    {{ t('automations-form-triggers-coupon_expiring') }}
                                </option>
                                <option
                                    v-if="
                                        ($module('automations') &&
                                        module('rewards') &&
                                        pointsProgram()) ||
                                        trigger === 'reward_reached'
                                    "
                                    value="reward_reached"
                                >
                                    {{ t('automations-form-triggers-reward_reached') }}
                                </option>
                                <option
                                    v-if="
                                        ($module('automations') &&
                                        module('rewards') &&
                                        pointsProgram()) ||
                                        trigger === 'reward_used'
                                    "
                                    value="reward_used"
                                >
                                    {{ t('automations-form-triggers-reward_used') }}
                                </option>
                                <option
                                    v-if="
                                        $module('automations') &&
                                        module('forms')
                                    "
                                    value="feedback_received"
                                >
                                    {{ t('automations-form-triggers-feedback_received') }}
                                </option>
                                <option
                                    v-if="
                                        $module('automations') &&
                                        module('segments')
                                    "
                                    value="joined_segment"
                                >
                                    {{ t('automations-form-triggers-joined_segment') }}
                                </option>
                                <option
                                    v-if="
                                        $module('automations') &&
                                        module('segments')
                                    "
                                    value="left_segment"
                                >
                                    {{ t('automations-form-triggers-left_segment') }}
                                </option>
                                <option
                                    v-if="$module('automations')"
                                    value="enter_stage"
                                >
                                    {{ t('automations-form-triggers-enter_stage') }}
                                </option>
                                <option
                                    v-if="
                                        $module('automations') &&
                                        module('tasks')
                                    "
                                    value="task_moved_to_column"
                                >
                                    {{
                                        t(
                                            "automations-form-triggers-task_moved_to_column",
                                        )
                                    }}
                                </option>
                                <option
                                    v-if="$module('automations')"
                                    value="birthday"
                                >
                                    {{ t('automations-form-triggers-birthday') }}
                                </option>
                                <option
                                    v-if="$module('automations')"
                                    value="manual"
                                >
                                    {{ t('automations-form-triggers-manual') }}
                                </option>
                                <option
                                    v-if="$module('campaigns')"
                                    value="specific_date"
                                >
                                    {{ t('automations-form-triggers-specific_date') }}
                                </option>
                                <option
                                    v-if="$module('quotations')"
                                    value="new_quotation"
                                >
                                    {{ t('automations-form-triggers-new_quotation') }}
                                </option>
                                <option
                                    v-if="$module('quotations')"
                                    value="quotation_status_change"
                                >
                                    {{ t('automations-form-triggers-quotation_status_change') }}
                                </option>
                                <option
                                    v-if="$module('quotations')"
                                    value="quotations_too_long_status"
                                >
                                    {{ t('automations-form-triggers-quotations_too_long_status') }}
                                </option>
                                <option
                                    v-if="$module('quotations')"
                                    value="quotation_expired"
                                >
                                    {{ t('automations-form-triggers-quotation_expired') }}
                                </option>
                                <option
                                    v-if="$module('quotations')"
                                    value="quotation_without_order"
                                >
                                {{ t('automations-form-triggers-quotation_without_order') }}
                                </option>
                            </select>

                            <div
                                v-if="
                                    (trigger === 'reward_reached' || trigger === 'reward_used') &&
                                    !pointsProgram()
                                "
                                class="alert alert-warning mt-2"
                            >
                                {{ t('automations-form-trigger_points_program_inactive_warning') }}
                            </div>

                            <component
                                :is="triggerSettingsComponent"
                                :value="conditions"
                                v-bind="triggerSettingsProps"
                                @input="updateConditions"
                                ref="triggerSettings"
                            ></component>
                        </div>
                        <info-modal
                            id="add-action-modal"
                            ref="addActionModal"
                            :isDismissible="false"
                        >
                            <template slot="title">{{ t('automations-form-add_action_modal_title') }}</template>
                            <template slot="body">
                                <div>
                                    <div class="btn-list">
                                            <button
                                                v-if="
                                                    module('coupons')
                                                    &&
                                                    !plugin('tray')
                                                "
                                                @click.prevent="
                                                    addAction('coupon')
                                                "
                                            >
                                                {{ t('automations-form-action_types-generate_coupon') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                v-if="
                                                    module('coupons')
                                                    && plugin('tray')
                                                "
                                                @click.prevent="
                                                    addAction(
                                                        'coupon_external',
                                                        {
                                                            properties: {
                                                                plugin: 'tray',
                                                            },
                                                        },
                                                    )
                                                "
                                            >
                                                {{ t('automations-form-action_types-generate_coupon_tray') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                v-if="
                                                    module('coupons') &&
                                                    plugin('shopify')
                                                "
                                                @click.prevent="
                                                    addAction(
                                                        'coupon_external',
                                                        {
                                                            properties: {
                                                                plugin: 'shopify',
                                                                allow_percentage: true
                                                            },
                                                        },
                                                    )
                                                "
                                            >
                                                {{ t('automations-form-action_types-generate_coupon_shopify') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                v-if="
                                                    module('coupons') && [
                                                        'inactivity',
                                                        'order_status_change',
                                                        'new_order',
                                                        'too_long_on_external_status'
                                                ].includes(trigger)"

                                                @click.prevent="
                                                    addAction('cashback')
                                                "
                                            >
                                                {{ t('automations-form-action_types-calculate_cashback') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                v-if="module('forms')"
                                                @click.prevent="
                                                    addAction('feedback_link')
                                                "
                                            >
                                                {{ t('automations-form-action_types-generate_feedback_link') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                v-if="
                                                    module('coupons') ||
                                                    module('rewards')
                                                "
                                                @click.prevent="
                                                    addAction('auth_link')
                                                "
                                            >
                                                {{ t('automations-form-action_types-generate_auth_link') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                v-if="
                                                    module('sms') ||
                                                    module('email') ||
                                                    module('rcs')
                                                "
                                                @click.prevent="
                                                    addAction('notification')
                                                "
                                            >
                                                {{ t('automations-form-action_types-generate_notification') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                v-if="module('rewards') && pointsProgram()"
                                                @click.prevent="
                                                    addAction('add_points')
                                                "
                                            >
                                                {{ t('automations-form-action_types-add_points') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                v-if="module('tasks')"
                                                @click.prevent="
                                                    addAction('task')
                                                "
                                            >
                                                {{ t('automations-form-action_types-add_task') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                v-if="module('client_fields')"
                                                @click.prevent="
                                                    addAction('update_field')
                                                "
                                            >
                                                {{ t('automations-form-action_types-update_client_field') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                v-if="module('web_hook')"
                                                @click.prevent="
                                                    addAction('web_hook')
                                                "
                                            >
                                                {{ t('automations-form-action_types-webhook') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                v-if="module('ai')"
                                                @click.prevent="
                                                    addAction('ai_content')
                                                "
                                            >
                                                {{ t('automations-form-action_types-generate_ai_content') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                @click.prevent="
                                                    addAction(
                                                        'move_client_stage',
                                                    )
                                                "
                                            >
                                                {{ t('automations-form-action_types-move_client_stage') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                v-if="$module('segments')"
                                                @click.prevent="
                                                    addAction('add_to_segment')
                                                "
                                            >
                                                {{ t('automations-form-action_types-add_to_segment') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                v-if="$module('segments')"
                                                @click.prevent="
                                                    addAction('remove_from_segment')
                                                "
                                            >
                                                {{ t('automations-form-action_types-remove_from_segment') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                v-if="user.global_admin"
                                                @click.prevent="
                                                    addAction('smart_insertion')
                                                "
                                            >
                                                {{ t('automations-form-action_types-smart_insertion') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                v-if="user.global_admin"
                                                @click.prevent="
                                                    addAction(
                                                        'remove_smart_insertion',
                                                    )
                                                "
                                            >
                                                {{ t('automations-form-action_types-remove_smart_insertion') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                @click.prevent="
                                                    addAction('wait')
                                                "
                                            >
                                                {{ t('automations-form-action_types-wait') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                @click.prevent="
                                                    addAction('data_sets')
                                                "
                                            >
                                                {{ t('automations-form-action_types-select_data_sets') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                @click.prevent="
                                                    addAction('calculator')
                                                "
                                            >
                                                {{ t('automations-form-action_types-calculator') }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                            <button
                                                v-for="action in pluginActions"
                                                :key="
                                                    action.plugins_places_id +
                                                    '_' +
                                                    action.id
                                                "
                                                @click.prevent="
                                                    addAction('plugin_action', {
                                                        properties: { action },
                                                        alias: pluginActionAlias(action),
                                                    })
                                                "
                                            >
                                                {{ action.name }}
                                                <svg width="8" height="12" fill="transparent">
                                                    <use href="#arrow-right"/>
                                                </svg>
                                            </button>
                                    </div>
                                </div>
                            </template>
                        </info-modal>
                    </div>
                    <div class="target-settings">
                        <div class="title-row">
                            <h2>{{ t('automations-form-filter_audience_title') }}</h2>
                            <div class="total-audience">
                                <span class="far fa-users"></span>
                                {{ t('automations-form-audience_label') }}
                                  <strong v-if="loadingTarget"><span class="far fa fa-spinner fa-spin"></span></strong>
                                  <strong v-else>{{ estimatedTarget }}</strong>
                            </div>
                        </div>
                        <div
                            v-if="hasPointsBalance && !pointsProgram()"
                            class="alert alert-warning mb-3"
                        >
                            {{ t('automations-form-filter_points_program_inactive_warning') }}
                        </div>
                        <div class="filter-section">
                            <filter-bar
                                v-show="ready"
                                @changed="estimateTarget"
                                ref="filter-bar"
                                :update-query-string="false"
                                :has-period-filter="false"
                                :has-branch-filter="true"
                                :is-vertical="true"
                                :show-labels="true"
                                :filters = "extraFilters"
                            />
                            <span v-show="!ready" class="far fa-spinner fa-spin"></span>
                        </div>
                    </div>
                    <template>
                        <div class="actions-settings">
                            <h2>
                                {{ t('automations-form-configure_actions_title') }}
                                <button
                                    class="btn btn-primary"
                                    @click.prevent="$refs.addActionModal.open()"
                                >
                                    {{ t('automations-form-add_button') }}
                                </button>
                            </h2>
                            <p>
                                {{ t('automations-form-action_config_help') }}
                            </p>
                            <confirm
                                v-for="(action, key) in actions"
                                :key="`confirmDeleteTemplate-${key}`"
                                :id="`confirmDeleteTemplate-${key}`"
                                :ref="`confirmDeleteTemplate-${key}`"
                                @confirm="deleteAction(key)"
                            >
                                <template slot="title">
                                    <span>
                                        {{ t('automations-form-confirm_delete_action_title', [action.form["alias"]]) }}
                                    </span>
                                </template>
                                <template slot="body">
                                    <div>
                                        {{ t('automations-form-confirm_delete_action_body') }}
                                    </div>
                                </template>
                            </confirm>
                            <confirm
                                key="confirmSave"
                                ref="confirmSave"
                                @confirm="save(true)"
                            >
                                <template slot="title">
                                    <span>
                                        {{ t('automations-form-confirm_save_campaign_title') }}
                                    </span>
                                </template>
                                <template slot="body">
                                    <div>
                                        {{ t('automations-form-confirm_save_campaign_body') }}
                                    </div>
                                </template>
                            </confirm>
                            <div class="actions">
                                <draggable
                                    :list="actions"
                                    group="actions"
                                    v-model="actions"
                                    :options="{
                                        group: 'actions',
                                        handle: '.handle',
                                    }"
                                >
                                    <automation-action
                                        v-for="(action, key) in actions"
                                        :key="'action-' + action.key"
                                        v-model="action.form"
                                        :type="action.type"
                                        :id="key + ''"
                                        ref="actions"
                                        @closed="updateIsValid"
                                        @confirm-delete="handleConfirmDelete"
                                        @deleted="deleteAction(key)"
                                    />
                                </draggable>
                            </div>
                        </div>
                    </template>
                    <button
                        class="btn btn-success btn-save-automation"
                        :disabled="!isValid || saveIsLoading"
                        @click.prevent="confirmSave()"
                    >
                        {{ saveIsLoading ? t('automations-form-saving') : t('automations-form-save_and_activate') }}
                    </button>
                    <button
                        v-if="trigger !== 'specific_date'"
                        class="btn btn-light btn-save-automation2"
                        :disabled="!isValid"
                        @click.prevent="save(false)"
                    >
                        {{ t('automations-form-save_draft') }}
                    </button>
                </form>
            </div>
        </div>
    </div>
</template>
<style lang="scss" scoped>
.title-row,
.filter-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.actions .sortable-ghost {
    opacity: 0.5;
}

.btn-list button {
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-align: left;
    width: 100%;

    svg {
        margin-left: auto;
        flex-shrink: 0;
        order: 2;
    }
}
</style>
<script>
import store from "../store";
import keviApi from "../keviApi";
import $ from "jquery";

store.registerModule("automationForm", {
    namespaced: true,
    state: {
        actions: [],
        trigger: "",
        conditions: [],
        keyCounter: 0,
    },
    getters: {
        tokens: (state, getters, rootState) => (actionId, sendMethod = null) => {
            const pointsProgramActive = rootState.user.points_program_active === true;

            let tokens = [
                "cliente.nome",
                "cliente.sobrenome",
                "cliente.email",
                "cliente.telefone",
                ...(pointsProgramActive ? ["cliente.pontos"] : []),
                "cliente.url_descadastro",
                "negocio.nome",
                "negocio.telefone",
            ];

            const unsubscribeIndex = tokens.indexOf("cliente.url_descadastro") + 1;

            let currentSendMethod = sendMethod;
            if (actionId !== undefined && actionId !== null) {
                const currentAction = state.actions[parseInt(actionId)];
                if (currentAction && currentAction.type === "notification") {
                    currentSendMethod = currentSendMethod || currentAction.form?.properties?.sendMethod;
                }
            }

            if (currentSendMethod === "email") {
                tokens.splice(unsubscribeIndex, 0, "cliente.url_descadastro_email");
            } else if (currentSendMethod === "sms") {
                tokens.splice(unsubscribeIndex, 0, "cliente.url_descadastro_sms");
            } else if (currentSendMethod === "rcs") {
                tokens.splice(unsubscribeIndex, 0, "cliente.url_descadastro_rcs");
            } else if (currentSendMethod === "whatsapp" || currentSendMethod === "whatsapp-cloud") {
                tokens.splice(unsubscribeIndex, 0, "cliente.url_descadastro_whatsapp");
            }

            let triggerTokens = [];
            switch (state.trigger) {
                case "new_order":
                    triggerTokens = pointsProgramActive
                        ? ["pedido.pontos", "pedido.valor"]
                        : ["pedido.valor"];
                    break;
                case "feedback_received":
                    triggerTokens = ["resposta.nota"];
                    break;
                case "task_moved_to_column":
                    triggerTokens = [
                        "tarefa.titulo",
                        "tarefa.descricao",
                        "tarefa.files_urls",
                        "tarefa.resposta.files_urls",
                        "tarefa.resposta.nota",
                        "quadro.nome",
                        "coluna.nome",
                    ];
                    break;
                case "reward_reached":
                    triggerTokens = [
                        "recompensa.preco",
                        "recompensa.descricao",
                    ];
                    break;
                case "reward_used":
                    triggerTokens = [
                        "recompensa.preco",
                        "recompensa.descricao",
                    ];
                    break;
                case "coupon_expiring":
                    triggerTokens = [
                        "cupom_expirando.codigo",
                        "cupom_expirando.descricao",
                        "cupom_expirando.valido_ate",
                        "cupom_expirando.automacao.id",
                        "cupom_expirando.automacao.titulo",
                        "cupom_expirando.automacao.gatilho"
                    ];
                    break;
                case "manual": {
                    const conditions = state.conditions?.[0] || {};
                    triggerTokens = (conditions.fields || [])
                        .filter((f) => f.key)
                        .map((f) => "campos." + f.key);
                    break;
                }
            }
            tokens = [...tokens, ...triggerTokens];

            let actions = state.actions;
            for (let i = 0; i < actions.length; i++) {
                let action = actions[i];
                if (parseInt(i) === parseInt(actionId)) {
                    break;
                }
                let actionTokens = [];
                switch (action.type) {
                    case "feedback_link":
                    case "auth_link":
                        actionTokens = ["url", "codigo"];
                        break;
                    case "ai_content":
                        actionTokens = ["conteudo"];
                        if (action.form?.properties?.outputFields?.length) {
                            actionTokens = [
                                "conteudo",
                                ...action.form.properties.outputFields
                                    .filter(f => f.key)
                                    .map(f => f.key)
                            ];
                        }
                        break;
                    case "coupon":
                    case "coupon_external":
                        actionTokens = [
                            "codigo",
                            "descricao",
                            "valido_ate",
                            "desconto",
                            "pedido_minimo",
                        ];
                        break;
                    case "cashback":
                        actionTokens = ["desconto", "pedido_minimo"];
                        break;
                    case "plugin_action": {
                        actionTokens = Object.keys(
                            action.form.properties.action.availableSnippets,
                        );
                        break;
                    }
                    case "data_sets": {
                        actionTokens = action?.form?.properties?.tokens || [];
                        break;
                    }
                    case "calculator": {
                        actionTokens = ["resultado"];
                        break;
                    }
                }
                let addAlias = (alias) => (value) => alias + "." + value;
                actionTokens = actionTokens.map(addAlias(action.form.alias));
                tokens = [...tokens, ...actionTokens];
            }
            return tokens.map((t) => "[[" + t + "]]");
        },
    },
    actions: {
        deleteAction({ state, commit }, key) {
            let actions = state.actions.map((a) => ({ ...a }));
            actions.splice(key, 1);
            commit("setActions", actions);
        },
        addAction({ state, commit }, args) {
            let actions = state.actions.map((a) => ({ ...a }));
            actions.push({
                type: args.action,
                key: state.keyCounter,
                form: {
                    ...{
                        alias: "",
                        conditions: [],
                        properties: {},
                    },
                    ...args.options,
                },
            });
            commit("setKeyCounter", state.keyCounter + 1);
            commit("setActions", actions);
        },
    },
    mutations: {
        setTrigger(state, trigger) {
            state.trigger = trigger;
        },
        setConditionsSchema(state, conditions) {
            state.conditions = conditions;
        },
        setActions(state, actions) {
            state.actions = actions;
        },
        setProperties(state, key, properties) {
            let actions = state.actions.map((a) => ({ ...a }));
            actions[key]["properties"] = properties;
            state.actions = actions;
        },
        setConditions(state, key, conditions) {
            let actions = state.actions.map((a) => ({ ...a }));
            actions[key]["conditions"] = conditions;
            state.actions = actions;
        },
        setKeyCounter(state, keyCounter) {
            state.keyCounter = keyCounter;
        },
    },
});

import birthday from "./triggers/birthday/birthday";
import clientDate from "./triggers/client_date/client_date";
import periodic from "./triggers/periodic/periodic";
import couponExpiring from "./triggers/coupon_expiring/coupon_expiring";
import customEvent from "./triggers/custom-event/custom-event";
import customEventWithoutOrder from "./triggers/custom-event-without-order/custom-event-without-order.vue";
import FilterBar from "../components/reports/filter-bar";
import dataFilled from "./triggers/data_filled/data_filled";
import feedbackReceived from "./triggers/feedback_received/feedback_received";
import inactivity from "./triggers/inactivity/inactivity";
import manual from "./triggers/manual/manual.vue";
import newOrder from "./triggers/new-order/new-order";
import orderStatusChange from "./triggers/order-status-change/order-status-change";
import tooLongOnExternalStatus from "./triggers/too-long-on-status/too-long-on-status";
import joinedSegment from "./triggers/joined-segment/joined-segment";
import leftSegment from "./triggers/joined-segment/left-segment";
import rewardReached from "./triggers/reward_reached/reward_reached";
import rewardUsed from "./triggers/reward_used/reward_used";
import specificDate from "./triggers/specific_date/specific_date";
import enterStage from "./triggers/enter_stage/enter_stage";
import taskMovedToColumn from "./triggers/task_moved_to_column/task_moved_to_column.vue";
import action from "./actions/action";
import Select from "../components/reports/filters/select.vue";
import { mapGetters } from "vuex";
import productGroupStock from "./triggers/product-group-stock/product-group-stock";
import PeriodBetweenExecution from "./filters/period_between_executions";
import newQuotation from "./triggers/new-quotation/new-quotation.vue";
import quotationStatusChange from "./triggers/quotation-status-change/quotation-status-change.vue";
import quotationsTooLongStatus from "./triggers/quotations-too-long-status/quotations-too-long-status.vue";
import quotationExpired from "./triggers/quotation-expired/quotation-expired.vue";
import quotationWithoutOrder from "./triggers/quotation-without-order/quotation-without-order.vue";
FilterBar.components["period-between-execution"] = PeriodBetweenExecution;
import draggable from "vuedraggable";
import debounce from "../helpers/debounce";

export default {
    props: {
        type: {
            type: String,
            default: "new",
        },
    },
    components: {
        draggable,
        "automation-trigger-birthday": birthday,
        "automation-trigger-client-date": clientDate,
        "automation-trigger-periodic": periodic,
        "automation-trigger-coupon-expiring": couponExpiring,
        "automation-trigger-data-filled": dataFilled,
        "automation-trigger-feedback-received": feedbackReceived,
        "automation-trigger-inactivity": inactivity,
        "automation-trigger-manual": manual,
        "automation-trigger-new-order": newOrder,
        "automation-trigger-order-status-change": orderStatusChange,
        "automation-trigger-custom-event": customEvent,
        "automation-trigger-custom-event-without-order": customEventWithoutOrder,
        "automation-trigger-joined-segment": joinedSegment,
        "automation-trigger-left-segment": leftSegment,
        "automation-trigger-reward-reached": rewardReached,
        "automation-trigger-reward-used": rewardUsed,
        "automation-trigger-specific-date": specificDate,
        "automation-trigger-too-long-on-external-status": tooLongOnExternalStatus,
        "automation-trigger-product-group-stock": productGroupStock,
        "automation-trigger-enter-stage": enterStage,
        "automation-trigger-task-moved-to-column": taskMovedToColumn,
        "automation-trigger-new-quotation": newQuotation,
        "automation-trigger-quotation-status-change":  quotationStatusChange,
        "automation-trigger-quotations-too-long-status": quotationsTooLongStatus,
        "automation-trigger-quotation-expired": quotationExpired,
        "automation-trigger-quotation-without-order": quotationWithoutOrder,
        "automation-action": action,
        FilterBar,
        Select,
    },
    data: function () {
        return {
            id: null,
            title: "",
            locked_by: null,
            locked_at: null,
            active: null,
            trigger: "",
            triggerValid: false,
            conditions: [],
            target: {},
            estimatedTarget: null,
            isValid: false,
            keyCounter: 0,
            state: "ready",
            goalIdOptions: [],
            goal: null,
            goalId: null,
            conversionPeriod: 7,
            pluginActions: [],
            previousRequest: null,
            loadingTarget: false,
            extraFilters: [],
            ready: false,
            saveIsLoading: false
        };
    },
    async created() {
        document.getElementsByTagName("title")[0].innerText = this.t("automations-form-page_title");
        this.getPluginActions();
        window.setInterval(() => this.updateIsValid(), 3000); //Hack to solve bug where sometimes the save button isn't show with reactivity
        await this.loadData();

        if (this.locked_by !== null) {
            this.confirmEditAutomation();
        }

        if (this.trigger === "specific_date" && this.active == 0) {
            this.confirmEditActiveCampaign();
        }

        if (this.$route.params.id) {
            this.$http(keviApi.getOptions("automations/" + this.$route.params.id + "/lock", "POST"));
        }
        $("body").tooltip({ selector: "[data-toggle=tooltip]" });

        if (this.$route.params.id) {
            window.addEventListener("mousemove", this.handleUserActivity);
            window.addEventListener("keypress", this.handleUserActivity);
            window.addEventListener("beforeunload", this.handleBeforeUnload);
        }
    },
    mounted() {
        if (!this.$route.params.id) {
            this.resetFilters();
            this.estimateTarget();
        }
    },
    // eslint-disable-next-line vue/no-deprecated-destroyed-lifecycle
    beforeDestroy() {
        if (this.id) {
            this.$http(keviApi.getOptions("automations/" + this.id + "/unlock", "POST"));
            window.removeEventListener("mousemove", this.handleUserActivity);
            window.removeEventListener("keypress", this.handleUserActivity);
            window.removeEventListener("beforeunload", this.handleBeforeUnload);
        }
    },
    computed: {
        actions: function () {
            return this.$store.state.automationForm.actions;
        },
        triggerSettingsComponent: function () {
            return this.trigger
                ? "automation-trigger-" + this.trigger.replace(/[_ ]/gm, "-")
                : false;
        },
        triggerSettingsProps: function () {
            if (this.trigger === "manual") {
                return { automationId: this.id };
            }
            return {};
        },
        place() {
            return this.$store.state.place;
        },
        user() {
            return this.$store.state.user;
        },
        ...mapGetters(["module", "plugin", "pointsProgram"]),
        goals() {
            const goals = {
                orders: this.t("automations-form-goals-orders"),
                answers: this.t("automations-form-goals-answers"),
                custom_event_clients: this.t("automations-form-goals-custom_event_clients"),
            };

            if (this.pointsProgram() || this.goal === "loyalty_points_earned") {
                goals.loyalty_points_earned = this.t("automations-form-goals-loyalty_points_earned");
            }

            if (this.pointsProgram() || this.goal === "reward_redemptions") {
                goals.reward_redemptions = this.t("automations-form-goals-reward_redemptions");
            }

            return goals;
        },
        hasPointsBalance() {
            return Boolean(
                this.target &&
                typeof this.target === "object" &&
                !Array.isArray(this.target) &&
                this.target["points-balance"] !== undefined &&
                this.target["points-balance"] !== null
            );
        },
    },
    methods: {
        confirmSave() {
            const hasValidValue = (val) => {
                return val !== null && val !== "" && val !== false && val !== "this" && val !== "all";
            };

            const hasFilters = this.target !== null && Object.values(this.target).some(value => {
                if (typeof value === "object" && value !== null) {
                    return Object.values(value).some(subValue => hasValidValue(subValue));
                }
                return hasValidValue(value);
            });

            if (this.trigger === "specific_date" && !hasFilters) {
                this.$refs["confirmSave"].open();
            } else {
                this.save(true);
            }
        },
        handleUserActivity: debounce(function () {
            this.$http(keviApi.getOptions("automations/" + this.id + "/lock", "POST"));
        }, 1000, true),
        handleBeforeUnload() {
            this.$http(keviApi.getOptions("automations/" + this.id + "/unlock", "POST"));
        },
        is: function (state) {
            return this.state === state;
        },
        handleConfirmDelete: function (actionId) {
            this.$refs["confirmDeleteTemplate-" + actionId][0].open();
        },
        confirmEditAutomation: function () {
            this.$refs["confirmEditAutomation"].open();
        },
        confirmEditActiveCampaign: function () {
            this.$refs["confirmEditActiveCampaign"].open();
        },
        back: function () {
            this.$router.go(-1);
        },
        escapeHtml(value) {
            return String(value ?? "")
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;");
        },
        capitalizeFirstLetter(string) {
            if (!string) return this.t('automations-form-a_user');
            return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
        },
        getConfirmOverwriteTitle() {
            return this.t("automations-form-confirm_overwrite_title", [
                this.escapeHtml(this.capitalizeFirstLetter(this.locked_by?.name)),
            ]);
        },
        setState: function (state) {
            this.state = state;
        },
        getPluginActions() {
            const options = keviApi.getOptions("automations/plugin-actions");
            this.$http(options).then((response) => {
                this.pluginActions = response.data;
            });
        },
        async resetFilters(value = null) {
            this.extraFilters = [];
            const filterBar = this.$refs["filter-bar"];
            filterBar.setValues({});
            this.extraFilters.push({
                component: "min-max",
                label: this.$root.t('filter_bar-avg_ticket'),
                name: "avg-ticket",
                removable: true,
            });

            this.extraFilters.push({
                component: "min-max",
                label: this.$root.t('filter_bar-orders_amount'),
                name: "orders-amount",
                options: {
                    mask: "######",
                },
                removable: true,
            });

            this.extraFilters.push({
                component: "selectbox",
                label: this.$root.t('filter_bar-customer_profile'),
                name: "customer-profile",
                options: [
                    { value: "1", label: this.$root.t('filter_bar-with_complete_registration') },
                    { value: "0", label: this.$root.t('filter_bar-without_complete_registration') },
                ],
                removable: true,
            });

            const targetToCheck = value || this.target;
            const hasPointsBalance = Boolean(
                targetToCheck &&
                typeof targetToCheck === "object" &&
                !Array.isArray(targetToCheck) &&
                targetToCheck["points-balance"] !== undefined &&
                targetToCheck["points-balance"] !== null
            );

            if (this.pointsProgram() || hasPointsBalance) {
                this.extraFilters.push({
                    component: "min-max",
                    label: this.$root.t('filter_bar-points_balance'),
                    name: "points-balance",
                    options: {
                        mask: "######",
                    },
                    removable: true,
                });
            }

            if (this.$module("segments")) {
              let options = keviApi.getOptions("segments");
              const response = await this.$http(options);
              if (response.body) {
                let segments = response.body;
                let segmentOptions = segments.map((seg) => ({
                  value: seg.id,
                  label: seg.name + (seg.error_reason ? ` (${this.t('automations-form-segment_with_error')})` : ''),
                }));
                this.extraFilters.push({
                  component: "checkbox",
                  label: this.$root.t('filter_bar-client_segments'),
                  name: "client-segments",
                  options: {
                    options: segmentOptions,
                    icon: "fa-tag",
                  },
                  removable: true,
                });
                this.extraFilters.push({
                  component: "checkbox",
                  label: this.$root.t('filter_bar-client_segments_not'),
                  name: "client-segments-not",
                  options: {
                    options: segmentOptions,
                    icon: "fa-ban",
                  },
                  removable: true,
                });
              }
            }

            this.extraFilters.push({
                component: "min-max-dates",
                label: this.$root.t('filter_bar-last_order_period'),
                name: "last-order-period",
                removable: true,
            });

            this.extraFilters.push({
                component: "period-between-execution",
                label: this.$root.t('filter_bar-min_period_between_executions'),
                name: "min-period-between-execution",
                removable: true,
            });

            if (this.$store.state.place.branches.length > 1) {
                this.extraFilters.push({
                    component: "branch",
                    label: this.$root.t('filter_bar-branches'),
                    removable: true,
                    name: "branch",
                });
            }

            if (this.$store.state.place.branches.length > 1) {
                this.extraFilters.push({
                    component: "branch",
                    label: this.$root.t('filter_bar-last_order_branch'),
                    removable: true,
                    name: "last_branch",
                });
            }

            if (
                this.trigger === "specific_date" ||
                this.trigger === "periodic"
            ) {
                this.extraFilters.push({
                    component: "number",
                    label: this.$root.t('filter_bar-clients_limit'),
                    name: "clients-limit",
                    options: {
                        mask: "######",
                        label: this.$root.t('filter_bar-value'),
                    },
                    removable: true,
                });
            }

            if (value && !Array.isArray(value)) {
                this.$refs["filter-bar"].setValues(value);
                this.estimateTarget(value);
            }
            this.ready = true;
        },
        estimateTarget(filters) {
            if (filters) {
                this.target = filters;
            } else {
                this.target = {};
            }

            if (
                !this.pointsProgram() &&
                (!filters ||
                    filters["points-balance"] === undefined ||
                    filters["points-balance"] === null)
            ) {
                this.extraFilters = this.extraFilters.filter(
                    (f) => f.name !== "points-balance"
                );
            }

            var options = keviApi.getOptions(
                "automations/estimate-target",
                "GET",
                {
                    filters: filters,
                    automationId: this.id,
                },
            );
            options.before = (request) => {
                this.loadingTarget = true;
                if (this.previousRequest) {
                    this.previousRequest.abort();
                }
                this.previousRequest = request;
            };
            this.$http(options)
                .then(function (response) {
                    this.estimatedTarget = response.body.target;
                    this.loadingTarget = false;
                })
                .catch((error) => {
                    this.loadingTarget = false;
                    console.error("Erro ao estimar target:", error);
                });
        },
        async loadData() {
            this.id = null;
            this.$store.commit("automationForm/setActions", []);
            if (this.$route.params.id) {
                this.setState("loading");
                let options = keviApi.getOptions("automations/" + this.$route.params.id);
                try {
                    let response = await this.$http(options);
                    let result = response.body;
                    console.log(result);
                    this.id = result.id;
                    this.locked_at = result.locked_at;
                    this.locked_by = result.locked_by;
                    this.active = result.active;
                    this.title = result.title;
                    this.trigger = result.trigger;
                    this.target = result.target;
                    this.goal = result.goal;
                    this.goalId = result.goal_id;
                    this.conversionPeriod = result.conversion_period;
                    let actions = [];
                    for (let a in result.actions) {
                        let action = result.actions[a];
                        actions.push({
                            id: action.id,
                            key: action.id,
                            type: action.type,
                            form: {
                                alias: action.alias,
                                conditions: JSON.parse(JSON.stringify(action.conditions)),
                                properties: JSON.parse(JSON.stringify(action.properties)),
                            },
                        });
                    }

                    this.$store.commit("automationForm/setActions", actions);
                    this.setState("ready");
                    await new Promise((resolve) => setTimeout(resolve, 100));
                    this.conditions = result.conditions;
                    this.resetFilters(result.target);
                } catch (error) {
                    console.error("Erro ao carregar dados:", error);
                }
            }
        },
        /**
         * Alias padrão de uma ação de plugin: a tradução em automations.action_alias (o id da ação
         * se não houver), com no máximo 25 caracteres e sem repetir um alias já usado na automação.
         */
        pluginActionAlias: function (action) {
            const key = "automations-action_alias-" + action.id;
            const translated = this.t(key);
            const base = (translated !== key ? translated : String(action.id)).slice(0, 25);
            const used = this.$store.state.automationForm.actions.map((a) => a.form && a.form.alias);
            let alias = base;
            for (let i = 2; used.includes(alias); i++) {
                const suffix = "_" + i;
                alias = base.slice(0, 25 - suffix.length) + suffix;
            }
            return alias;
        },
        addAction: function (action, options = null) {
            this.$store.dispatch("automationForm/addAction", {
                action: action,
                options: options,
            });
            this.$refs.addActionModal.close();
            window.setTimeout(() => {
                this.$refs.actions[this.$refs.actions.length - 1].openModal();
            }, 50);
        },
        actionChangedConditions: function (key) {
            return (conditions) => {
                this.$store.commit(
                    "automationForm/setConditions",
                    key,
                    conditions,
                );
            };
        },
        actionChangedProperties: function (key) {
            return (properties) => {
                this.$store.commit(
                    "automationForm/setProperties",
                    key,
                    properties,
                );
            };
        },
        deleteAction: function (key) {
            this.$store.dispatch("automationForm/deleteAction", key);
        },
        updateIsValid: function () {
            let allActionsValid = true;
            let totalActions = 0;
            if (this.$refs.actions) {
                totalActions = this.$refs.actions.length;
                let invalidActions = this.$refs.actions
                    .map((action) => action.isValid())
                    .filter((isValid) => !isValid);
                if (invalidActions.length > 0) {
                    allActionsValid = false;
                }
            } else {
                allActionsValid = false;
            }
            if (
                this.title.length > 0 &&
                this.trigger.length > 0 &&
                this.triggerValid &&
                allActionsValid &&
                totalActions > 0 &&
                this.goal
            ) {
                this.isValid = true;
            } else {
                this.isValid = false;
            }
        },
        updateConditions: function (conditions) {
            this.conditions = conditions;
        },
        save: function (active = true) {
            if (this.isValid) {
                this.saveIsLoading = true;
                let data = {
                    id: this.id,
                    title: this.title,
                    trigger: this.trigger,
                    active: active,
                    conditions: JSON.parse(JSON.stringify(this.conditions)),
                    actions: JSON.parse(JSON.stringify(this.actions)),
                    target: JSON.parse(JSON.stringify(this.target)),
                    goal: this.goal,
                    goal_id: this.goalId,
                    conversion_period: this.conversionPeriod,
                };
                let options = keviApi.getOptions(
                    "automations/save",
                    "POST",
                    data,
                );
                this.$http(options).then((response) => {
                    if (response.status === 200) {
                        window.mixpanel?.track(
                            `${data.id ? "Update" : "Create"} Automation`,
                            {
                                automation_id: data.id || null,
                                automation_title: data.title,
                                automation_trigger: data.trigger,
                                automation_active: data.active,
                                automation_conditions: data.conditions,
                                automation_actions: data.actions,
                                automation_target: data.target,
                                place_id: this.place.id,
                                place_name: this.user.place_name,
                                place_branch_name: this.user.branch_title,
                                user_name: this.user.name,
                                user_username: this.user.username,
                                user_email: this.user.email,
                            },
                        );
                        this.$router.push("/automations");
                    }
                }).catch((error) => {
                    console.error("Erro ao salvar:", error);
                    this.$store.dispatch("notification/error", this.t("automations-form-save_error_message"));
                })
                .finally(() => {
                    this.saveIsLoading = false;
                });
            }
        },
        updateSelecteGoalList: function () {
            if (["answers", "custom_event_clients"].includes(this.goal)) {
                let labels = {
                    custom_event_clients: "title",
                    answers: "name",
                };
                let url = {
                    custom_event_clients: "custom-event/events",
                    answers: "forms",
                };
                var options = keviApi.getOptions(url[this.goal]);
                this.$http(options).then(function (response) {
                    if (response.status === 200) {
                        this.goalIdOptions = response.body.map((ev) => {
                            return { label: ev[labels[this.goal]], id: ev.id };
                        });
                    } else {
                        this.goalIdOptions = [];
                    }
                    if (
                        !this.goalIdOptions
                            .map((g) => g.id)
                            .includes(this.goalId)
                    ) {
                        this.goalId = null;
                    }
                });
            } else {
                this.goalId = null;
            }
        },
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
    },
    watch: {
        goal: function () {
            this.updateSelecteGoalList();
        },
        trigger: function () {
            window.setTimeout(() => this.updateIsValid(), 100);
            this.resetFilters();
            this.$store.commit("automationForm/setTrigger", this.trigger);
        },
        conditions: function () {
            this.triggerValid =
                this.trigger &&
                this.$refs.triggerSettings &&
                this.$refs.triggerSettings.isValid
                    ? this.$refs.triggerSettings.isValid()
                    : false;
            this.$store.commit("automationForm/setConditionsSchema", this.conditions);
            window.setTimeout(() => this.updateIsValid(), 100);
        },
        title: function () {
            window.setTimeout(() => this.updateIsValid(), 100);
        },
        actions: function () {
            window.setTimeout(() => this.updateIsValid(), 100);
        },
    },
};
</script>
